import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { components as rsComponents } from 'react-select'
import { MarketCalculusCard } from '../../../components/preview/Preview'
import { RSelect } from '../../../components/Component'
import { useGlobalStoreData } from '../../../store/globalStore'
import { RegistryFirmsServices } from '../../../services/McDataServices'
import { useSDSsearchParams, useSDSisVisible } from '../../../store/searchDataStore'
import FirmListServerSide from '../../../mailTemplate/src/components/atomic/organisms/FirmListServerSide'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Tooltip } from '@mui/material'
import { countryEN } from '../../../assets/data/country-en'
import { countryTR } from '../../../assets/data/country-tr'
import '../../../assets/scss/apps/_serverside-table.scss'
import { sortTregFirms } from '../McPageHelpers'

const SELECT_ALL_OPTION = { code: '__select_all__', __selectAll: true }
const LIMIT_TAGS = 2

const LimitedMultiValue = (props) => {
  const { index } = props
  const count = props.getValue().length

  if (index < LIMIT_TAGS) {
    return <rsComponents.MultiValue {...props} />
  }

  if (index === LIMIT_TAGS) {
    return (
      <span style={{ fontSize: '0.875rem', marginLeft: 4 }}>
        +{count - LIMIT_TAGS}
      </span>
    )
  }

  return null
}

const TradeRegistryTabs = ({ id, dropdownData, tableData, setTableData, selectedOptions, setSelectedOptions, setFilters, isActive }) => {
  const { t } = useTranslation()

  const searchParams = useSDSsearchParams()
  const isSearchPanelVisible = useSDSisVisible()

  const languageGSD = useGlobalStoreData.language()

  const [rawData, setRawData] = useState({})
  const [loading, setLoading] = useState(false)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [serverSidePagination, setServerSidePagination] = useState({ pageSize: 10 })
  const [importerCountries, setImporterCountries] = useState([])
  const [exporterCountries, setExporterCountries] = useState([])

  const requestRef = useRef(null)
  const hasOpenedOnceRef = useRef(false)

  const countryData = useMemo(() => {
    const countries = languageGSD === 'tr' ? countryTR : countryEN
    return countries.sort((a, b) => a.countryName.localeCompare(b.countryName))
  }, [languageGSD])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText === '' || searchText.length >= 3) {
        setDebouncedSearchText(searchText)
      }
    }, 800)

    return () => {
      clearTimeout(handler)
    }
  }, [searchText])

  useEffect(() => {
    if (!selectedOptions.length) {
      setTableData(id, [])
      return
    }

    const controller = new AbortController()
    requestRef.current = controller

    const fetchData = async () => {
      try {
        setLoading(true)
        const payload = {
          currentCountryCode: searchParams.yourCountry,
          hsCodes: searchParams.hsCode,
          page: serverSidePagination.pageIndex ?? 0,
          ...serverSidePagination,
          registryFirmFilter: {
            searchKeyword: debouncedSearchText || undefined,
            countryCodes: searchParams.countryToExport ? [searchParams.countryToExport] : undefined,
            sectorCodes: selectedOptions.map(opt => opt.code),
            ...(importerCountries.length > 0 && { firmImporterCountryCodes: importerCountries.map(c => c.countryCode) }),
            ...(exporterCountries.length > 0 && { firmExporterCountryCodes: exporterCountries.map(c => c.countryCode) })
          }
        }
        const response = await RegistryFirmsServices.getFirms(payload, { signal: controller.signal })
        if (!controller.signal.aborted) {
          setRawData(response || [])
          setTableData(id, response?.registryFirms || [])
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Data fetch error:', error)
          setTableData(id, [])
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    fetchData()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, serverSidePagination, debouncedSearchText, importerCountries, exporterCountries])

  const sortedDropdownData = useMemo(() => sortTregFirms(dropdownData), [dropdownData])

  const allSelected = sortedDropdownData.length > 0 && selectedOptions.length === sortedDropdownData.length

  const optionsWithSelectAll = useMemo(() => {
    if (!sortedDropdownData?.length) return []

    return [SELECT_ALL_OPTION, ...sortedDropdownData]
  }, [sortedDropdownData])

  const clearAll = () => {
    setSelectedOptions(id, [])
    setSearchText('')
    setDebouncedSearchText('')
    setImporterCountries([])
    setExporterCountries([])
  }

  const handleChange = (selected, actionMeta) => {
    if (!selected || selected.length === 0) {
      clearAll()
      return
    }

    const isSelectAllAction = actionMeta?.option?.__selectAll
    if (isSelectAllAction) {
      if (allSelected) {
        clearAll()
      } else {
        setSelectedOptions(id, sortedDropdownData)
      }
      return
    }

    setSelectedOptions(id, selected)
  }

  useEffect(() => {
    if (isActive && !selectedOptions.length && !hasOpenedOnceRef.current) {
      setMenuIsOpen(true)
      hasOpenedOnceRef.current = true
    }
  }, [isActive, selectedOptions.length])

  useEffect(() => {
    if (isSearchPanelVisible) {
      setMenuIsOpen(false)
    }
  }, [isSearchPanelVisible])

  useEffect(() => {
    if (setFilters) {
      setFilters(id, {
        searchKeyword: debouncedSearchText || undefined,
        firmImporterCountryCodes: importerCountries.length > 0 ? importerCountries.map(c => c.countryCode) : undefined,
        firmExporterCountryCodes: exporterCountries.length > 0 ? exporterCountries.map(c => c.countryCode) : undefined
      })
    }
  }, [id, debouncedSearchText, importerCountries, exporterCountries, setFilters])

  return (
    <MarketCalculusCard>
      <div className="flex align-items-center mb-3">
        <h5 className="mb-0">{t('mailTemplate.wizard.firmList.sector')}</h5>
        <div style={{ minWidth: 250, paddingInlineStart: 10 }}>
          <RSelect
            id="firmOptions"
            options={optionsWithSelectAll}
            isMulti
            value={selectedOptions}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            onChange={handleChange}
            getOptionValue={opt => opt.code}
            getOptionLabel={opt => opt.__selectAll ? t('mailTemplate.wizard.firmList.selectAllSectors') : `${opt.code}${opt?.name?.[languageGSD] ? `-${opt.name[languageGSD]}` : ''}`}
            menuIsOpen={isActive ? menuIsOpen : false}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}

            formatOptionLabel={(opt, { context }) => {
              if (opt.__selectAll) {
                return (
                  <span style={{ fontWeight: 600, borderBottom: '1px solid #e0e0e0', display: 'block', paddingBottom: 4 }}>
                    {t('mailTemplate.wizard.firmList.selectAllSectors')}
                  </span>
                )
              }

              const label = `${opt.code}${opt?.name?.[languageGSD] ? `-${opt.name[languageGSD]}` : ''}`

              if (context === 'value') return opt.code

              return (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                  <span
                    style={{
                      flex: 1,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      fontWeight: opt.relevant ? 700 : 400,
                      lineHeight: 1.3
                    }}
                  >
                    {label}
                  </span>
                  {opt.relevant && (
                    <Tooltip title={t('mailTemplate.campaignReview.relevant')} placement="right">
                      <CheckCircleOutlineIcon fontSize="small" color="success" style={{ marginLeft: 8 }}/>
                    </Tooltip>
                  )}
                </div>
              )
            }}

            styles={{
              option: (base, state) => {
                const isSelectAll = state.data?.__selectAll
                const isSelectAllHighlighted = isSelectAll && allSelected

                return {
                  ...base,
                  display: 'flex',
                  alignItems: 'flex-start',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.3,
                  fontWeight: isSelectAll ? 600 : (state.data?.relevant ? 700 : 400),
                  backgroundColor: isSelectAllHighlighted
                    ? (state.isFocused ? '#DEEBFF' : '#E8F0FE')
                    : base.backgroundColor,
                  color: isSelectAllHighlighted ? '#2684FF' : base.color
                }
              },
              menu: base => ({
                ...base,
                maxWidth: 420
              }),
              menuList: base => ({
                ...base,
                maxHeight: 320
              }),
              menuPortal: base => ({
                ...base,
                zIndex: 1300
              })
            }}

            components={{ MultiValue: LimitedMultiValue }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <div style={{ paddingInlineStart: 10 }}>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder={t('table_search_by_firm_name_or_profile')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={!selectedOptions?.length}
            style={{ width: 220 }}
          />
        </div>
        <div style={{ paddingInlineStart: 10, width: 220 }}>
          <RSelect
            id="importerCountries"
            options={countryData || []}
            isMulti
            value={importerCountries}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            onChange={(val) => {
              setImporterCountries(val || [])
              setServerSidePagination(prev => ({ ...prev, pageIndex: 0 }))
            }}
            getOptionValue={opt => opt.countryCode}
            getOptionLabel={opt => opt.countryName}
            formatOptionLabel={(opt, { context }) => context === 'value' ? opt.countryCode : opt.countryName}
            isDisabled={loading || !selectedOptions?.length}
            placeholder={t('mailTemplate.wizard.firmList.filterImportCountries')}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: base => ({ ...base, zIndex: 1300 })
            }}
          />
        </div>
        <div style={{ paddingInlineStart: 10, width: 220 }}>
          <RSelect
            id="exporterCountries"
            options={countryData || []}
            isMulti
            value={exporterCountries}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            onChange={(val) => {
              setExporterCountries(val || [])
              setServerSidePagination(prev => ({ ...prev, pageIndex: 0 }))
            }}
            getOptionValue={opt => opt.countryCode}
            getOptionLabel={opt => opt.countryName}
            formatOptionLabel={(opt, { context }) => context === 'value' ? opt.countryCode : opt.countryName}
            isDisabled={loading || !selectedOptions?.length}
            placeholder={t('mailTemplate.wizard.firmList.filterExportCountries')}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: base => ({ ...base, zIndex: 1300 })
            }}
          />
        </div>
      </div>
      <div className="my-3">
        <FirmListServerSide
          setServerSidePaginationByKey={setServerSidePagination}
          serverSidePagination={serverSidePagination}
          data={tableData || []}
          rowCount={rawData?.totalRecordCount || 0}
          fetching={loading}
          languageGSD={languageGSD}
          className="serverside-table"
        />
      </div>
    </MarketCalculusCard>
  )
}

export default TradeRegistryTabs
